import {
  APP_INITIALIZER,
  FactoryProvider,
  Injectable,
  Injector,
  NgModuleRef,
  Type,
  createNgModule
} from '@angular/core';

import { Observable, ReplaySubject } from 'rxjs';

import { EnvironmentService } from '@hdms/shared/util-core';

export interface PageFrame {
  module: NgModuleRef<unknown>;
  header: Type<unknown>;
  footer: Type<unknown>;
  globalBanner: Type<unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class PageFrameLoaderService {
  private readonly pageFrameFactory$ = new ReplaySubject<PageFrame>(1);

  constructor(private readonly injector: Injector) {}

  public getPageFrame$(): Observable<PageFrame> {
    return this.pageFrameFactory$.asObservable();
  }

  /**
   * In default Hybris context we don't need header/footer so let's lazy-load
   * header/footer module and component so they don't add size to main bundle.
   * The final components will be emitted via observable.
   */
  async load(): Promise<void> {
    const { SharedPageFrameFeatureModule, PageHeaderComponent, PageFooterComponent, GlobalBannerComponent } =
      await import('@hdms/shared/page-frame/feature');

    this.pageFrameFactory$.next({
      module: createNgModule(SharedPageFrameFeatureModule, this.injector),
      header: PageHeaderComponent,
      footer: PageFooterComponent,
      globalBanner: GlobalBannerComponent
    });
  }
}

/**
 * Loads the header module before app init, if in standalone mode
 */
export const PAGE_FRAME_PROVIDER: FactoryProvider = {
  provide: APP_INITIALIZER,
  useFactory: initModuleLoading,
  deps: [EnvironmentService, PageFrameLoaderService],
  multi: true
};

function initModuleLoading(
  { isStandalone, hostContext }: EnvironmentService,
  loader: PageFrameLoaderService
): () => Promise<void> {
  return () => (isStandalone || hostContext ? loader.load() : Promise.resolve());
}
